// client/src/coms/e2ee/E2eeReportsPanel.js
// Personal Settings -> Privacy & Security: your own reports about encrypted messages (status only for you), and,
// for safety moderators only, the queue of reports about encrypted direct messages with decisions and the
// direct-message suspension. Server channel reports are reviewed in Server Settings -> Moderation instead.
import React, { useCallback, useEffect, useState } from 'react';
import { comsApiGet, comsApiPatch, comsApiPost } from '../comsApi';
import ReportEvidence from './ReportEvidence';
import './e2ee.css';

function SafetyQueue() {
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [notes, setNotes] = useState({});
  const [msg, setMsg] = useState('');
  const load = useCallback(() => {
    comsApiGet(`/safety/reports?status=${status}`)
      .then((r) => setReports((r && r.reports) || []))
      .catch((e) => setMsg(String((e && e.message) || e)));
  }, [status]);
  useEffect(() => load(), [load]);
  const decide = async (r, outcome) => {
    setMsg('');
    try {
      await comsApiPatch(`/safety/reports/${r.id}`, { status: 'closed', outcome, resolution: notes[r.id] || '' });
      setMsg('Decision recorded.');
      load();
    } catch (e) {
      setMsg(String((e && e.data && e.data.error) || (e && e.message) || e));
    }
  };
  const suspend = async (r) => {
    setMsg('');
    try {
      const s = await comsApiPost(`/safety/reports/${r.id}/suspend-dms`, { hours: 24 });
      setMsg(`Direct messages suspended until ${String(s.until).replace('T', ' ').slice(0, 16)} UTC.`);
      load();
    } catch (e) {
      setMsg(String((e && e.data && e.data.error) || (e && e.message) || e));
    }
  };
  return (
    <div className="vx-e2ee-explain" data-testid="safety-queue">
      <strong>Safety moderation: reports about encrypted direct messages</strong>
      <span className="vx-set-hint">You see only the messages reporters chose to disclose. Everything else stays encrypted.</span>
      <div className="vx-e2ee-confirm-row">
        <button type="button" className={`vx-btn small${status === 'open' ? ' is-on' : ''}`} onClick={() => setStatus('open')}>
          Open
        </button>
        <button type="button" className={`vx-btn small${status === 'closed' ? ' is-on' : ''}`} onClick={() => setStatus('closed')}>
          Closed
        </button>
      </div>
      {msg ? <span className="vx-set-hint" role="status">{msg}</span> : null}
      {!reports.length ? <span className="vx-set-hint">No {status} reports.</span> : null}
      {reports.map((r) => (
        <div key={r.id} className="vx-e2ee-evidence-item" data-testid={`safety-report-${r.id}`}>
          <span>
            <strong>{r.reason}</strong> · {r.reporter_name} reported {r.target_name} · {String(r.created_at || '').slice(0, 16)}
          </span>
          {r.details ? <span className="vx-set-hint">“{r.details}”</span> : null}
          <ReportEvidence evidence={r.evidence} names={{ [r.target_user_id]: r.target_name, [r.reporter_user_id]: r.reporter_name }} />
          {r.status === 'open' ? (
            <>
              <input className="vx-e2ee-input" placeholder="Decision note (optional)" value={notes[r.id] || ''} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} />
              <div className="vx-e2ee-confirm-row">
                <button type="button" className="vx-btn small danger" onClick={() => suspend(r)} data-testid={`safety-suspend-${r.id}`}>
                  {r.target_dm_suspended_until ? 'Extend DM suspension (24 h)' : 'Suspend their DMs (24 h)'}
                </button>
                <button type="button" className="vx-btn small" onClick={() => decide(r, 'actioned')} data-testid={`safety-actioned-${r.id}`}>
                  Close: action taken
                </button>
                <button type="button" className="vx-btn small" onClick={() => decide(r, 'no_action')}>
                  Close: no action
                </button>
                <button type="button" className="vx-btn small" onClick={() => decide(r, 'unfounded')}>
                  Close: unfounded
                </button>
              </div>
            </>
          ) : (
            <span className="vx-set-hint">Decision: {r.resolution || 'closed'}</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function E2eeReportsPanel() {
  const [mine, setMine] = useState([]);
  const [isSafety, setIsSafety] = useState(false);
  useEffect(() => {
    comsApiGet('/reports/e2ee/mine')
      .then((r) => setMine((r && r.reports) || []))
      .catch(() => {});
    comsApiGet('/safety/role')
      .then((r) => setIsSafety(!!(r && r.safety_moderator)))
      .catch(() => {});
  }, []);
  if (!mine.length && !isSafety) return null;
  return (
    <div className="vx-set-block" id="vx-setting-e2ee-reports" data-testid="e2ee-reports-panel">
      {mine.length ? (
        <div className="vx-e2ee-explain" data-testid="my-e2ee-reports">
          <strong>Your reports about encrypted messages</strong>
          {mine.map((r) => (
            <span key={r.id}>
              {String(r.created_at || '').slice(0, 16)} · {r.reason} · about {r.target_name} · {r.evidence && r.evidence.items ? r.evidence.items.length : 0} message(s) ·{' '}
              {r.status === 'open' ? 'waiting for review' : `closed${r.resolution ? ` (${r.resolution.split(':')[0].replace('_', ' ')})` : ''}`}
            </span>
          ))}
        </div>
      ) : null}
      {isSafety ? <SafetyQueue /> : null}
    </div>
  );
}
